import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  ClipboardCheck, 
  Send,
  User,
  ArrowLeft
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MinhasAvaliacoes180 = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [avaliacaoSelecionada, setAvaliacaoSelecionada] = useState(null);
  const [respostas, setRespostas] = useState({});

  useEffect(() => {
    carregarMinhasAvaliacoes();
  }, []);

  const carregarMinhasAvaliacoes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/feedback180-v2/minhas-avaliacoes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvaliacoes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar avaliações:', error);
      toast.error('Erro ao carregar avaliações');
    } finally {
      setLoading(false);
    }
  };

  const iniciarAvaliacao = (avaliacao) => {
    setAvaliacaoSelecionada(avaliacao);
    // Inicializar respostas vazias
    const respostasIniciais = {};
    avaliacao.perguntas.forEach((_, index) => {
      respostasIniciais[index] = 0;
    });
    setRespostas(respostasIniciais);
  };

  const voltarParaLista = () => {
    setAvaliacaoSelecionada(null);
    setRespostas({});
  };

  const selecionarResposta = (index, valor) => {
    setRespostas({
      ...respostas,
      [index]: valor
    });
  };

  const enviarRespostas = async () => {
    // Validar se todas as perguntas foram respondidas
    const todasRespondidas = Object.values(respostas).every(r => r > 0);
    if (!todasRespondidas) {
      toast.error('Por favor, responda todas as 20 perguntas');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Formatar respostas
      const respostasFormatadas = avaliacaoSelecionada.perguntas.map((pergunta, index) => ({
        pergunta_index: index,
        pergunta: pergunta.pergunta,
        resposta: respostas[index]
      }));

      await axios.post(
        `${API}/api/feedback180-v2/${avaliacaoSelecionada.id}/responder`,
        respostasFormatadas,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Avaliação respondida com sucesso!');
      
      // Voltar para lista e recarregar
      setAvaliacaoSelecionada(null);
      setRespostas({});
      await carregarMinhasAvaliacoes();
      
    } catch (error) {
      console.error('Erro ao enviar respostas:', error);
      toast.error('Erro ao enviar respostas');
    } finally {
      setLoading(false);
    }
  };

  // Se estiver respondendo uma avaliação
  if (avaliacaoSelecionada) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={voltarParaLista}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Avaliando: {avaliacaoSelecionada.avaliado_nome}
            </h1>
            <p className="text-gray-600 text-sm">
              {avaliacaoSelecionada.tipo_avaliacao === 'colaborador_avalia_gestor' 
                ? 'Você está avaliando seu gestor' 
                : 'Seu gestor está te avaliando'}
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Responda as 20 perguntas (Escala de 1 a 5)</CardTitle>
            <p className="text-sm text-gray-600">
              1 = Discordo Totalmente | 5 = Concordo Totalmente
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {avaliacaoSelecionada.perguntas.map((pergunta, index) => (
              <div key={index} className="border-b pb-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 mb-1">
                      {index + 1}. {pergunta.pergunta}
                    </p>
                    <Badge variant="secondary" className="text-xs">
                      {pergunta.competencia}
                    </Badge>
                  </div>
                </div>

                {/* Escala Likert */}
                <div className="flex items-center space-x-2 mt-3">
                  {[1, 2, 3, 4, 5].map((valor) => (
                    <button
                      key={valor}
                      onClick={() => selecionarResposta(index, valor)}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                        respostas[index] === valor
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-blue-300 text-gray-600'
                      }`}
                    >
                      {valor}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1 px-2">
                  <span>Discordo</span>
                  <span>Neutro</span>
                  <span>Concordo</span>
                </div>
              </div>
            ))}

            <div className="flex justify-end pt-4">
              <Button
                onClick={enviarRespostas}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
                size="lg"
              >
                <Send className="h-5 w-5 mr-2" />
                {loading ? 'Enviando...' : 'Enviar Avaliação'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Lista de avaliações pendentes
  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <ClipboardCheck className="h-8 w-8 mr-3 text-blue-600" />
          Minhas Avaliações
        </h1>
        <p className="text-gray-600 mt-1">
          Avaliações 180º pendentes para você responder
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Carregando avaliações...</p>
            </div>
          ) : avaliacoes.length === 0 ? (
            <div className="text-center py-8">
              <ClipboardCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhuma avaliação pendente
              </h3>
              <p className="text-gray-600">
                Você não tem avaliações 180º para responder no momento
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {avaliacoes.map((avaliacao) => (
                <div
                  key={avaliacao.id}
                  className="border-2 border-blue-200 rounded-lg p-6 hover:bg-blue-50 transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {avaliacao.avaliado_nome}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {avaliacao.tipo_avaliacao === 'colaborador_avalia_gestor'
                            ? '🎯 Avaliar seu Gestor'
                            : '🎯 Ser avaliado pelo Gestor'}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-yellow-700 border-yellow-300">
                      Pendente
                    </Badge>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Competências avaliadas:</strong>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {avaliacao.competencias_selecionadas.map((comp, idx) => (
                        <Badge key={idx} variant="secondary">
                          {comp}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <p className="text-sm text-gray-600">
                      20 perguntas • Escala de 1 a 5
                    </p>
                    <Button
                      onClick={() => iniciarAvaliacao(avaliacao)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <ClipboardCheck className="h-4 w-4 mr-2" />
                      Responder Agora
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MinhasAvaliacoes180;
